import {
	ChangeTriggerKind,
	Form,
	FormattedText,
	FormItem,
	SearchInput,
	SearchInputHandle,
	Typography
} from "client/Components";
import {
	AlertManager,
	KeyBoardListNavigationContext,
	MediaQueryKind,
	useAlerts,
	useKeyboardListNavigation,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { localize } from "client_legacy/ndaw/culture";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { tryParseInt } from "client/Misc/parseUtils";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	SearchIntegrationKind,
	SearchNodeModel,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	GlobalSearchTermModel,
	ScopeSearchNodeModel,
	deleteSearchTerms,
	listSearchTerms,
	registerSearchTerm
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import styles from "./SearchPage.module.less";
import {
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	connectToRouter
} from "client/Navigation";
import {
	isNilOrWhiteSpace,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { useGlobalSearchStore } from "../Layout/TitleBar/GlobalSearch/globalSearchStore";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	GlobalNavigationCriteria,
	useGlobalNavigationStore
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { never } from "client_legacy/ndaw/errors";
import {
	SearchNodeKind
} from "client/Api/SearchSettings/searchSettings";
import {
	getSearchQueryParams,
	ReactRouterUrlOptions,
	searchRouting
} from "./routing";
import { MultiScopeSearch } from "./SearchResults/MultiScopeSearch/MultiScopeSearch";
import { GroupView } from "./GroupView";
import { RecentSearch } from "./RecentSearch/RecentSearch";
import { ScopeView } from "./ScopeView";
import { GlobalSearchNodeTabs, isNodeSelected } from "../Layout/TitleBar/GlobalSearch/ResultView/GlobalSearchNodeTabs";
import { areSearchScopeIdentifiersEqual } from "../Layout/TitleBar/GlobalSearch/useSearchResultsScopeTabs";
import { convertToIdentifier } from "../Layout/TitleBar/GlobalSearch/useFetchGlobalSearchResults";
import { useSearchNodes } from "../Layout/TitleBar/GlobalSearch/useSearchNodes";
import { useFrequentlyUsedTileNavigation } from "../Layout/FrequentlyUsedTiles/useFrequentlyUsedTileNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { useRecentlyOpenedResults } from "./RecentSearch/Persistence/useRecentlyOpenedResults";
import { getActualRecentSearchQueries } from "../Layout/TitleBar/GlobalSearch/SearchAndResults/SearchAndResults";

export type SearchPageProps = {
	scope?: SearchIntegrationKind | null;
	integrationId?: number | null;
	groupId?: number | null;
	searchTerm?: string;
	onNavigate: NavigationCallback;
};

export const SearchPage = (props: SearchPageProps): ReactElement => {
	const {
		scope: scopeFromProps = null,
		integrationId = null,
		groupId = null,
		searchTerm: searchTermRouteValue = "",
		onNavigate
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: searchTermRouteValue,
		version: newGuid()
	});

	const [mobileSearchSettingsElement, setMobileSearchSettingsElement]= useState<HTMLDivElement | null>(null);

	const alertManager : AlertManager = useAlerts();
	const inputRef: RefObject<SearchInputHandle | null> = useRef<SearchInputHandle>(null);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const userTimezone: TimeZone = useUserTimezone();
	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	const [tileNavigationElement, invokeFrequentlyUsedTile] = useFrequentlyUsedTileNavigation(userTimezone);

	const {
		recentSearchQueries,
		setRecentSearchQueries
	} = useGlobalSearchStore(
		(x) => ({
			recentSearchQueries: x.recentSearchQueries,
			setRecentSearchQueries: x.setRecentSearchQueries
		}),
		shallowComparison
	);

	const {
		items: recentlyOpenedItems,
		remove: removeRecentlyOpenedItem,
		clear: clearRecentlyOpenedItems
	} = useRecentlyOpenedResults();

	const recentSearchQueriesRef: ReadonlyRefObject<readonly GlobalSearchTermModel[]> = useSynchronizedRef(recentSearchQueries);

	const pageTitle: string = localize(
		isResponsive
			? "_Search"
			: "_AdvancedSearch"
	);

	usePageTitle(pageTitle);

	const {
		nodes,
		allAvailableScopes
	} = useSearchNodes();

	useEffect(
		(): (() => void) => {
			return setActiveGlobalNavigationCriteria({
				type: "search"
			});
		},
		[setActiveGlobalNavigationCriteria]
	);

	const selectedNode = useMemo(
		(): SearchNodeModel | null => {
			if (nodes.length === 0) {
				return null;
			}

			if (isNotNil(scopeFromProps)) {
				const scope: TryResult<ScopeSearchNodeModel> = tryFindScope(
					{
						kind: GlobalSearchScopeKind.Predefined,
						predefinedKind: scopeFromProps
					},
					nodes
				);

				return scope.wasSuccessful
					? scope.result
					: null;
			}

			if (isNotNil(integrationId)) {
				const scope: TryResult<ScopeSearchNodeModel> = tryFindScope(
					{
						kind: GlobalSearchScopeKind.IntegrationFlow,
						integrationId
					},
					nodes
				);

				return scope.wasSuccessful
					? scope.result
					: null;
			}

			if (isNotNil(groupId)) {
				const result: SearchNodeModel | undefined = nodes
					.find(
						x => x.kind === SearchNodeKind.Group
							&& x.id === groupId
					);

				return result ?? null;
			}

			return null;
		},
		[
			groupId,
			integrationId,
			nodes,
			scopeFromProps
		]
	);

	const placeholderSuffix = useMemo(
		(): string => {
			if (selectedNode === null) {
				return localize("_AppsAnnouncementsFilesAndMore");
			}

			return selectedNode.name;
		},
		[selectedNode]
	);

	const focusInputHandler = useCallback(
		(): void => {
			inputRef.current
				?.focus();
		},
		[]
	);

	const searchHandler = useCallback(
		(value: string, version: Guid): void => {
			setSearchTerm({ value, version });

			if (isNilOrWhiteSpace(value)) {
				return;
			}

			async function registerSearchQuery(
				signal: AbortSignal
			): Promise<void> {
				try {
					const newSearchTerm: GlobalSearchTermModel = await registerSearchTerm(value, signal);

					setRecentSearchQueries([
						...recentSearchQueriesRef.current
							.filter(x => x.id !== newSearchTerm.id),
						newSearchTerm
					]);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			}

			registerSearchQuery(globalCancellation);
		},
		[
			globalCancellation,
			setRecentSearchQueries,
			recentSearchQueriesRef
		]
	);

	const selectedNodeChangeHandler = useCallback(
		(node: SearchNodeModel | null): void => {
			const url: Navigation = searchRouting.page({
				queryParams: getSearchQueryParams(
					getGlobalSearchQueryParamsOptions(
						searchTerm.value,
						node
					)
				)
			});

			onNavigate(url);
		},
		[
			onNavigate,
			searchTerm.value
		]
	);

	const scopeChangeHandler = useCallback(
		(scope: GlobalSearchScopeIdentifier): void => {
			const result = tryFindScope(scope, nodes);

			if (!result.wasSuccessful) {
				console.error(`Scope doesn't exist`);
				return;
			}

			selectedNodeChangeHandler(
				result.result
			);
		},
		[
			nodes,
			selectedNodeChangeHandler
		]
	);

	const deleteSearchHandler = useCallback(
		async (ids: readonly number[]): Promise<void> => {
			try {
				await deleteSearchTerms(ids, globalCancellation);

				setRecentSearchQueries(
					recentSearchQueries.filter(
						x => !ids.includes(x.id)
					)
				);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			alertManager,
			setRecentSearchQueries,
			globalCancellation,
			recentSearchQueries
		]
	);

	const {
		contextData: keyboardContextDate,
		focusFirstItemHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusInputHandler
	});

	useEffect(
		(): void => {
			setSearchTerm(
				(x) => {
					if (searchTermRouteValue === x.value) {
						return x;
					}

					return {
						value: searchTermRouteValue,
						version: newGuid()
					};
				}
			);
		},
		[searchTermRouteValue]
	);

	useEffect(
		(): void => {
			const url: Navigation = searchRouting.page({
				queryParams: {
					searchTerm: searchTerm.value,
					scope: scopeFromProps
						? `${scopeFromProps}`
						: undefined,
					groupId: isNotNil(groupId)
						? `${groupId}`
						: undefined,
					integrationId: isNotNil(integrationId)
						? `${integrationId}`
						: undefined,
				}
			});

			onNavigate(url);
		},
		[
			groupId,
			integrationId,
			onNavigate,
			scopeFromProps,
			searchTerm.value
		]
	);

	useEffect(
		(): () => void => {
			const fetchRecentSearchQueries = async(signal: AbortSignal): Promise<void> => {
				try {
					const response: readonly GlobalSearchTermModel[] = await listSearchTerms(signal);
					setRecentSearchQueries(response);
				} catch(e) {
					console.error(e);
				}
			};

			const cancellation = new AbortController();
			fetchRecentSearchQueries(cancellation.signal);

			return () => cancellation.abort();
		},
		[setRecentSearchQueries]
	);

	const candidateRecentSearchQueriesToRender = useMemo(
		(): readonly GlobalSearchTermModel[] => {
			return getActualRecentSearchQueries(recentSearchQueries);
		},
		[recentSearchQueries]
	);

	const topLevelNode = useMemo(
		(): SearchNodeModel | null => {
			if (!selectedNode) {
				return null;
			}

			const topLevelNode: SearchNodeModel | undefined = nodes
				.find(
					x => isNodeSelected(x, selectedNode)
				);

			if (!topLevelNode) {
				console.error(`Selected node doesn't exist in the node structure`);
				return null;
			}

			return topLevelNode;
		},
		[nodes, selectedNode]
	);

	const renderScopes = (): ReactElement | null => {
		if (!searchTerm.value) {
			return (
				<RecentSearch
					onSearch={searchHandler}
					onDeleteRecentSearchQueries={deleteSearchHandler}
					recentSearchQueries={candidateRecentSearchQueriesToRender}
					recentSearchResults={recentlyOpenedItems}
					onRemoveRecentSearchResult={removeRecentlyOpenedItem}
					onClearRecentSearchResults={clearRecentlyOpenedItems}
					isMobile={isMobile}
					onInvokeFrequentlyUsedTile={invokeFrequentlyUsedTile}
					timezone={userTimezone}
				/>
			);
		}

		if (!topLevelNode || !selectedNode) {
			return (
				<MultiScopeSearch
					searchTerm={searchTerm}
					scopes={allAvailableScopes}
					onViewMore={scopeChangeHandler}
				/>
			);
		}

		switch(topLevelNode.kind) {
			case SearchNodeKind.Group:
				if (topLevelNode.scopes.length === 1) {
					return (
						<ScopeView
							scope={topLevelNode.scopes[0]}
							mobileSearchSettingsElement={mobileSearchSettingsElement}
							searchTerm={searchTerm}
						/>
					);
				} else {
					return (
						<GroupView
							scopes={topLevelNode.scopes}
							searchTerm={searchTerm}
							selectedNode={selectedNode}
							onScopeChange={scope => isNotNil(scope)
								? scopeChangeHandler(scope)
								: selectedNodeChangeHandler(topLevelNode)
							}
							mobileSearchSettingsElement={mobileSearchSettingsElement}
							isMobile={isMobile}
						/>
					);
				}
			case SearchNodeKind.Scope:
				return (
					<ScopeView
						scope={topLevelNode}
						mobileSearchSettingsElement={mobileSearchSettingsElement}
						searchTerm={searchTerm}
					/>
				);
			default:
				never(topLevelNode);
		}
	};

	return (
		<>
			<div
				{...setTrackingId("advanced-search-page")}
				className={styles.root}
			>
				<div className={styles.content}>
					<div className={styles.header}>
						<h3 className={styles.pageTitle}>
							{pageTitle}
						</h3>

						<div className={styles.searchForm}>
							<Form onSubmit="NONE">
								<FormItem
									hasNoMargin
								>
									<SearchInput
										value={searchTerm}
										onChange={searchHandler}
										trigger={{ kind: ChangeTriggerKind.OnEnter }}
										autoFocus
										trackingId="search-term"
										placeholder={(
											<FormattedText
												text={localize("_SearchForProperties")}
												thingsToReplace={[
													{
														what: "{{Properties}}",
														filler: (
															<Typography weight="bold" isItalic>
																{placeholderSuffix}
															</Typography>
														)
													}
												]}
											/>
										)}
										ref={inputRef}
										onNavigateToResults={focusFirstItemHandler}
									/>
								</FormItem>
							</Form>

							<div
								className={styles.mobileSearchSettings}
								ref={e => setMobileSearchSettingsElement(e)}
							/>
						</div>

						<div className={styles.nodeTabs}>
							<GlobalSearchNodeTabs
								nodes={nodes}
								onSelectedNodeChange={selectedNodeChangeHandler}
								selectedNode={selectedNode}
								trackingId="advanced-search-node-tabs"
							/>
						</div>
					</div>
					<KeyBoardListNavigationContext.Provider value={keyboardContextDate}>
						{renderScopes()}
					</KeyBoardListNavigationContext.Provider>
				</div>
			</div>

			{tileNavigationElement}
		</>
	);
};
SearchPage.displayName = "SearchPage";

export function getGlobalSearchQueryParamsOptions(
	searchTerm: string,
	node: SearchNodeModel | null
): ReactRouterUrlOptions {
	const options: ReactRouterUrlOptions = {
		searchTerm,
		groupId: undefined,
		scope: undefined
	};

	if (node) {
		switch (node.kind) {
			case SearchNodeKind.Group:
				options.groupId = node.id;
				break;
			case SearchNodeKind.Scope:
				options.scope = convertToIdentifier(node);
				break;
			default:
				never(node);
		}
	}

	return options;
}

function tryFindScope(
	identifier: GlobalSearchScopeIdentifier,
	nodes: readonly SearchNodeModel[]
): TryResult<ScopeSearchNodeModel> {
	for (const node of nodes) {
		switch(node.kind) {
			case SearchNodeKind.Scope:
				if (areSearchScopeIdentifiersEqual(convertToIdentifier(node), identifier)) {
					return {
						wasSuccessful: true,
						result: node
					};
				}
				break;
			case SearchNodeKind.Group:
				for (const scope of node.scopes) {
					if (areSearchScopeIdentifiersEqual(convertToIdentifier(scope), identifier)) {
						return {
							wasSuccessful: true,
							result: scope
						};
					}
				}
				break;
			default:
				never(node);
		}
	}

	return { wasSuccessful: false };
}

function tryParseGlobalSearchScopeOrNull(rawValue: string | undefined): TryResult<SearchIntegrationKind | null> {
	if (rawValue === undefined || rawValue === "" || rawValue === "null") {
		return {
			wasSuccessful: true,
			result: null
		};
	}

	const intResult: TryResult<number> = tryParseInt(rawValue);

	if (intResult.wasSuccessful) {
		return {
			wasSuccessful: true,
			result: intResult.result
		};
	}

	return {
		wasSuccessful: false
	};
}

export type SearchPageQueryParams = QueryParametersFromProps<
	SearchPageProps,
	"groupId" | "scope" | "searchTerm" | "integrationId"
>;

export const SearchPageForRouter = connectToRouter(
	SearchPage,
	{
		"searchTerm": {
			isRequired: false,
			type: "query"
		},
		"groupId": {
			isRequired: false,
			type: "query",
			tryParse: tryParseInt,
			shouldIgnoreInvalidValues: true
		},
		"scope": {
			isRequired: false,
			type: "query",
			tryParse: tryParseGlobalSearchScopeOrNull,
			shouldIgnoreInvalidValues: true
		},
		"integrationId": {
			isRequired: false,
			type: "query",
			tryParse: tryParseInt,
			shouldIgnoreInvalidValues: true
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);